/* eslint-disable no-param-reassign */
import { createRouter, createWebHistory } from 'vue-router';
import { ref } from 'vue';
import usePageService from '@/services/pages';

const routes = [
  {
    path: '/',
    name: 'site-root',
    component: () => import('@/views/NotFoundView.vue'), // no base page so 404 for now
    meta: {},
  },
  {
    path: '/:pageId', // as rid
    props: true,
    name: 'landing-page',
    component: () => import('@/views/LandingPageView.vue'),
    beforeEnter: [getPageData],
    meta: {
      title: 'Landing Page',
    },
  },
  {
    path: '/preview/:pageId', // as id
    props: true,
    name: 'preview-landing-page',
    component: () => import('@/views/LandingPageView.vue'),
    beforeEnter: [getPreviewData],
    meta: {
      title: 'Preview Landing Page',
      preview: true,
    },
  },
  {
    path: '/edit',
    props: true,
    name: 'edit-landing-page',
    component: () => import('@/views/LandingPageView.vue'),
    meta: {
      title: 'Edit Landing Page',
      editing: true,
    },
  },
  {
    path: '/server-error',
    name: 'server-error',
    component: () => import('@/views/EndUserGenericErrorView.vue'),
    meta: {
      title: 'Server Error',
    },
  },
  {
    path: '/:pathMatch(.*)*',
    alias: '/not-found',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: {
      title: 'Page Not Found',
    },
  },
];

export const isLoading = ref(false);

const pageService = usePageService();

const errorNavigation = (next, payload) => {
  if (payload.response?.status === 404) {
    next({ name: 'not-found', params: { pathMatch: ['not-found'] } });
  }

  if (payload.response?.status === 500) {
    next({ name: 'server-error' });
  }

  if (!payload.data) {
    next({ name: 'not-found' });
  }
};

const getPageOptions = (next) => ({
  initialData: {},
  immediate: true,
  onFetchError(payload) {
    errorNavigation(next, payload);
  },
});

async function getPageData(to, from, next) {
  const { pageId } = to.params;

  const { data } = await pageService.getPage(pageId, getPageOptions(next));

  to.params.pageData = data.value.page;
  to.params.fontsData = data.value.fonts;
  to.params.affiliateData = data.value.affiliates;

  next();
}

async function getPreviewData(to, from, next) {
  const { pageId } = to.params;

  const { data } = await pageService.getPagePreview(pageId, getPageOptions(next));

  to.params.pageData = data.value.page;
  to.params.fontsData = data.value.fonts;
  to.params.affiliateData = data.value.affiliates;

  next();
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach((to) => {
  isLoading.value = true;

  const { title, description } = to.meta;
  // if no title is set in meta.title, set the default title
  const defaultTitle = 'Tagboard Landing Page';
  const defaultDescription = 'A simple landing page for your tagboard creations.';

  document.title = title || defaultTitle;

  const descriptionElement = document.querySelector('head meta[name="description"]');

  descriptionElement.setAttribute('content', description || defaultDescription);
});

router.afterEach(() => {
  isLoading.value = false;
});

export default router;
